import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import ChevronRightIcon from '~/components/MarketplaceIcons/ChevronRightIcon';
import { ProductCardCompact } from '~/components/ProductCardCompact';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import { useCategoryBlockProductList } from '~/components/ProductListAnalytics/useCategoryBlockProductList';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import {
  COMPACT_INLINE_LINK_CLASS,
  COMPACT_PRODUCT_ROW_CLASS,
  COMPACT_ROW_MAX_CARDS,
  COMPACT_SECTION_TITLE_CLASS,
} from './constants';
import { getBlockLink, splitFacts } from './utils';

interface Props {
  category: Category;
  /** Draw the collection on a plate in the block's CMS color; the facts row is desktop only. */
  withPlate?: boolean;
}

const MOBILE_COUNT = 6;

/**
 * Product collection of a compact home block: the block name, the facts from
 * its preview text, a "see all" link to the catalog category it was built
 * from and one full row of cards on desktop, as many as the width fits; two
 * columns of six on mobile, without the facts.
 */
const CompactProductsCollectionView = ({
  category,
  withPlate = false,
}: Props) => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();
  const list = useCategoryBlockProductList(category, 'grid');

  const products = category.products.slice(
    0,
    isMobile ? MOBILE_COUNT : COMPACT_ROW_MAX_CARDS,
  );

  if (products.length === 0) {
    return null;
  }

  const link = getBlockLink(category);
  const plateColor = withPlate
    ? category.backgroundColor || undefined
    : undefined;
  const facts = isMobile ? [] : splitFacts(category.previewText);

  return (
    <ProductListAnalyticsProvider list={list}>
      <section
        className={cn(
          'mt-7',
          plateColor &&
            (isMobile
              ? 'rounded-[18px] px-3 py-3.5'
              : 'rounded-[18px] px-[22px] py-5'),
        )}
        style={plateColor ? { backgroundColor: plateColor } : undefined}
      >
        <div
          className={cn(
            'mb-3',
            isMobile
              ? 'flex flex-wrap items-center gap-x-3 gap-y-2.5'
              : 'grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center gap-x-[22px]',
          )}
        >
          <h2
            className={cn(
              COMPACT_SECTION_TITLE_CLASS,
              'whitespace-nowrap',
              isMobile && 'flex-1',
            )}
          >
            {category.name}
          </h2>
          {!isMobile && (
            <span className="flex min-w-0 items-center justify-center gap-3 overflow-hidden">
              {facts.map((fact, index) => (
                <Fragment key={`${index}-${fact}`}>
                  {index > 0 && (
                    <span className="size-[3px] shrink-0 rounded-full bg-compact-dot" />
                  )}
                  <span className="flex h-5 shrink-0 items-center whitespace-nowrap text-[13px] text-compact-text-facts leading-none">
                    {fact}
                  </span>
                </Fragment>
              ))}
            </span>
          )}
          {link && (
            <Link
              to={link}
              className={cn(
                COMPACT_INLINE_LINK_CLASS,
                !isMobile && 'col-start-3',
              )}
            >
              {t('seeAll')}
              <ChevronRightIcon className="size-3.5" />
            </Link>
          )}
        </div>
        <div className={COMPACT_PRODUCT_ROW_CLASS}>
          {products.map((product, index) => (
            <ProductCardCompact
              key={product.id}
              product={product}
              index={index}
              borderColor={plateColor}
            />
          ))}
        </div>
      </section>
    </ProductListAnalyticsProvider>
  );
};

export const CompactProductsCollection = observer(
  CompactProductsCollectionView,
);
